/**
 * lib/ninetailed/mapExperiences.ts
 *
 * Pure mapper: turns the Contentful GraphQL `ntExperiencesCollection` shape
 * (see `MainBannerBlockFragment`) into the `ExperienceConfiguration[]` that
 * Ninetailed's `<Experience>` component consumes.
 *
 * Defensive by design (OWASP A04/A08): this is the single seam every
 * personalization render funnels through, and Contentful data can be partially
 * authored, unpublished, or malformed. On ANY unexpected input it returns `[]`
 * (i.e. "no experiences" → render the baseline) and NEVER throws, so a bad or
 * incomplete experience can never break the page.
 */

import { ExperienceMapper } from '@ninetailed/experience.js-utils';
import type { ExperienceLike } from '@ninetailed/experience.js-utils';
import type { Reference } from '@ninetailed/experience.js';

/** Minimal shape of a raw GraphQL experience entry (all fields optional/unknown). */
type RawAudience = {
  ntAudienceId?: string | null;
  ntName?: string | null;
  ntDescription?: string | null;
} | null;

type RawVariant = {
  sys?: { id?: string | null } | null;
  [key: string]: unknown;
} | null;

type RawExperience = {
  ntExperienceId?: string | null;
  ntName?: string | null;
  ntDescription?: string | null;
  ntType?: string | null;
  ntConfig?: unknown;
  ntAudience?: RawAudience;
  ntVariantsCollection?: { items?: Array<RawVariant> | null } | null;
} | null;

function toItemsArray(input: unknown): Array<RawExperience> | null {
  if (Array.isArray(input)) return input as Array<RawExperience>;
  if (input && Array.isArray((input as { items?: unknown }).items)) {
    return (input as { items: Array<RawExperience> }).items;
  }
  return null;
}

/**
 * Normalise a single raw GraphQL experience into an `ExperienceLike`, or return
 * `null` if it lacks the fields Ninetailed requires (id, usable variants).
 * Each variant's `id` must be the entry `sys.id`, which matches the ids inside
 * `ntConfig.components[].variants[].id`.
 */
function toExperienceLike(raw: RawExperience): ExperienceLike<Reference> | null {
  if (!raw || typeof raw !== 'object') return null;

  const id = raw.ntExperienceId;
  if (!id || typeof id !== 'string') return null;

  const rawVariants = raw.ntVariantsCollection?.items;
  if (!Array.isArray(rawVariants)) return null;

  const variants: Reference[] = rawVariants
    .filter((v): v is Exclude<RawVariant, null> => {
      return !!v && typeof v === 'object' && typeof v.sys?.id === 'string';
    })
    .map((v) => ({ ...v, id: v.sys!.id as string }));

  if (variants.length === 0) return null;

  const audience =
    raw.ntAudience && typeof raw.ntAudience.ntAudienceId === 'string'
      ? {
        id: raw.ntAudience.ntAudienceId,
        name: raw.ntAudience.ntName ?? undefined,
        description: raw.ntAudience.ntDescription ?? undefined,
      }
      : undefined;

  const config =
    raw.ntConfig && typeof raw.ntConfig === 'object'
      ? (raw.ntConfig as ExperienceLike<Reference>['config'])
      : undefined;

  return {
    id,
    name: raw.ntName ?? id,
    type: raw.ntType ?? 'nt_personalization',
    description: raw.ntDescription ?? undefined,
    config,
    audience,
    variants,
  };
}

/**
 * Map the raw GraphQL experiences collection to `ExperienceConfiguration[]`.
 * Returns `[]` (→ baseline) for any missing/malformed input and never throws.
 */
export function mapExperiences(
  input: unknown,
): ReturnType<typeof ExperienceMapper.mapExperience>[] {
  try {
    const items = toItemsArray(input);
    if (!items) return [];

    return items.reduce<ReturnType<typeof ExperienceMapper.mapExperience>[]>(
      (acc, raw) => {
        try {
          const like = toExperienceLike(raw);
          if (like && ExperienceMapper.isExperienceEntry(like)) {
            acc.push(ExperienceMapper.mapExperience(like));
          }
        } catch {
          // Skip a single malformed experience; keep the rest.
        }
        return acc;
      },
      [],
    );
  } catch {
    return [];
  }
}
